import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { renewAccessToken, retrieveTokens } from "@lib/jwt";
import {urls} from "@routes";


// ======================================================================
// ----------------------- SOME UTILITY FUNCTIONS -----------------------
// ======================================================================

export interface ApiRequestFailed {
    statusCode: number;
    statusText: string;
    data: any
}

function axiosInterceptorSuccessFn(response: AxiosResponse) {
    return response;
}

async function axiosInterceptorFailFn(error: AxiosError) {
    console.log(error.response);
    if (error.response?.status === 401) {
        const renewalSuccessful: boolean = await renewAccessToken();
        // Renew access token
        if (renewalSuccessful) {
            // send back the same error (status code 401) so that we can trigger the retry.
            return Promise.reject({
                statusCode: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data
            } as ApiRequestFailed);

        } else {
            // Failed to renew access token. Send back to login.
            console.log("Access token renewal has failed. Logging out.");
            window.location.replace(urls["logout"]);
        }

    } else if (error.response?.status === 301) {
        let data = error.response?.data as any;
        window.location.href = data["redirect_url"];

    } else {
        // some other error
        return Promise.reject({
            statusCode: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
        } as ApiRequestFailed);
    }
}

/**
 * Use this as retry function on all useQery and useMutation calls.
 */
export const retryFn = (failureCount: number, error: ApiRequestFailed) => {
    return (error.statusCode === 401) && failureCount <= 2;
}


// =============================================================================
// ----------------------- ADD YOUR APIS BELOW THIS LINE -----------------------
// =============================================================================

/**
 * Boilerplate code to fetch data from un-authenticated AiCallerBackend api endpoints.
 * Uses GET request. Returns Axios instance.
 *
 * @param apiPath - Abun DRF api endpoint path (ex: /get-some-data/). Starting forward slash is required.
 */
export function fetchData(apiPath: string) {
    const axiosInstance = axios.create();
    const axiosConfig: AxiosRequestConfig = {
        method: "get",
        url: process.env.REACT_APP_BACKEND + apiPath,
        responseType: "json",
    }

    axiosInstance.interceptors.response.use(axiosInterceptorSuccessFn, axiosInterceptorFailFn);
    return axiosInstance(axiosConfig);
}

/**
 * Boilerplate code to fetch data from secure AiCallerBackend api endpoints.
 * Uses GET request. Returns Axios instance.
 *
 * @param apiPath - Abun DRF api endpoint path (ex: /get-some-data/). Starting forward slash is required.
 */
export function authenticateAndFetchData(apiPath: string, responseType: AxiosRequestConfig['responseType'] = "json") {
    const tokens = retrieveTokens();
    const axiosInstance = axios.create();
    const axiosConfig: AxiosRequestConfig = {
        method: "get",
        url: process.env.REACT_APP_BACKEND + apiPath,
        responseType: responseType,
        withCredentials: true,
    }

    // Add auth header only if access token is available (not null). Otherwise CORS might incorrectly flag it as 401.
    if (tokens.access) {
        axiosConfig["headers"] = { 'Authorization': 'Bearer ' + tokens.access }
    }

    axiosInstance.interceptors.response.use(axiosInterceptorSuccessFn, axiosInterceptorFailFn);
    return axiosInstance(axiosConfig);
}

/**
 * Boilerplate code to authenticate and post data to AiCallerBackend api endpoints.
 *
 * @param apiPath - Abun DRF api endpoint path (ex: /get-some-data/). Starting forward slash is required.
 * @param data - json data to post.
 * @param method - HTTP method to use.
 */
export function authenticateAndPostData(apiPath: string, data: any, method: "post" | "patch" = "post") {
    const tokens = retrieveTokens();
    const axiosInstance = axios.create();
    const axiosConfig: AxiosRequestConfig = {
        method: method,
        url: process.env.REACT_APP_BACKEND + apiPath,
        responseType: "json",
        data: data,
        withCredentials: true,
    }

    // Add auth header only if access token is available (not null). Otherwise CORS might incorrectly flag it as 401.
    if (tokens.access) {
        axiosConfig["headers"] = { 'Authorization': 'Bearer ' + tokens.access }
    }

    axiosInstance.interceptors.response.use(axiosInterceptorSuccessFn, axiosInterceptorFailFn);
    return axiosInstance(axiosConfig);
}


export function authenticateAndPostFormData(apiPath: string, formData: FormData) {
    const tokens = retrieveTokens();
    const axiosInstance = axios.create();
    const axiosConfig: AxiosRequestConfig = {
        method: "post",
        url: process.env.REACT_APP_BACKEND + apiPath,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        responseType: "json",
        data: formData,
        withCredentials: true,
    }

    // Add auth header only if access token is available (not null). Otherwise CORS might incorrectly flag it as 401.
    if (tokens.access) {
        axiosConfig["headers"]!.Authorization = 'Bearer ' + tokens.access
    }

    axiosInstance.interceptors.response.use(axiosInterceptorSuccessFn, axiosInterceptorFailFn);
    return axiosInstance(axiosConfig);
}


// =============================================================================
// ----------------------- SPAMASSASSIN API INTEGRATION -----------------------
// =============================================================================

/**
 * SpamAssassin API request interface
 */
export interface SpamAssassinRequest {
    content: string;
    threshold?: number;
    subject?: string;
    from?: string;
    to?: string;
    date?: string;
    message_id?: string;
    reply_to?: string;
    cc?: string;
    bcc?: string;
}

/**
 * SpamAssassin API response interface
 */
export interface SpamAssassinResponse {
    is_spam: boolean;
    spam_score: number;
    report: string;
    threshold_used: number;
    parsed_output: {
        headers: {
            [key: string]: string;
        };
        spam_info: {
            status: string;
            score: number;
            spamassassin_threshold: number;
            tests: string[];
            is_spam: boolean;
        };
        content_analysis: any[];
        original_message: string;
        raw_output: string;
    };
}

/**
 * Call the external SpamAssassin API to check email content for spam
 *
 * @param data - SpamAssassin request data
 */
export function checkSpamContent(data: SpamAssassinRequest) {
    const axiosInstance = axios.create();
    const apiKey = process.env.REACT_APP_SPAM_CHECK_AUTH_KEY;

    const axiosConfig: AxiosRequestConfig = {
        method: "post",
        url: "https://spamcheck.deliveryman.ai/check",
        responseType: "json",
        data: data,
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
        }
    }

    // Using basic axios without interceptors since this is external API
    return axiosInstance(axiosConfig);
}


/**
 * Validates if a domain TLD is allowed by checking against the backend
 * @param tld The domain TLD to validate (without leading dot)
 * @returns Promise that resolves to true if domain TLD is allowed, false otherwise
 */
export async function validateDomainTLDAllowed(tld: string): Promise<boolean> {
    try {
        const response = await authenticateAndFetchData(`/admin/domain-tlds/`);
        const allowedDomainTLDs = response.data.tlds || [];

        // Check if the domain TLD exists
        const tldRecord = allowedDomainTLDs.find((t: any) => t.tld.toLowerCase() === tld.toLowerCase());
        return !!tldRecord;
    } catch (error) {
        console.error('Error validating domain TLD:', error);
        // In case of error, allow the TLD (fail open)
        return true;
    }
}


// =============================================================================
// ----------------------- MAINTENANCE MODE APIs -------------------------------
// =============================================================================

/**
 * Get maintenance mode status
 * @returns Promise with maintenance mode data
 */
export function getMaintenanceMode() {
    return authenticateAndFetchData("/admin/get-maintenance-mode/");
}

/**
 * Toggle maintenance mode on/off
 * @param isEnabled - Boolean indicating if maintenance mode should be enabled
 * @param message - Optional message to display during maintenance
 * @returns Promise with updated maintenance mode data
 */
export function toggleMaintenanceMode(isEnabled: boolean, message: string = "") {
    return authenticateAndPostData("/admin/toggle-maintenance-mode/", {
        is_enabled: isEnabled,
        message: message
    });
}

/**
 * Free email verification settings shape used by the admin settings page.
 */
export interface FreeEmailVerificationSettings {
    is_enabled: boolean;
    requests_per_minute_per_ip: number;
    max_emails_per_request: number;
    restrict_to_allowed_origins: boolean;
    allowed_origins: string[];
    updated_by_email: string | null;
    last_updated: string;
}

/**
 * Get the free email verification API configuration
 * @returns Promise with the current settings
 */
export function getFreeEmailVerificationSettings() {
    return authenticateAndFetchData("/admin/free-email-verification/settings/");
}

/**
 * Update the free email verification API configuration
 * @param settings - The settings values to save
 * @returns Promise with the updated settings
 */
export function updateFreeEmailVerificationSettings(settings: {
    is_enabled: boolean;
    requests_per_minute_per_ip: number;
    max_emails_per_request: number;
    restrict_to_allowed_origins: boolean;
    allowed_origins: string[];
}) {
    return authenticateAndPostData("/admin/free-email-verification/update-settings/", settings);
}

/**
 * Get free email verification usage stats
 * @param days - Size of the reporting window in days
 * @returns Promise with window totals, lifetime totals and recent requests
 */
export function getFreeEmailVerificationStats(days: number = 30) {
    return authenticateAndFetchData(`/admin/free-email-verification/stats/?days=${days}`);
}
